import NiceModal from '@ebay/nice-modal-react';

import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

import AutoComplete from 'components/field/AutoComplete';
import EnumSelect from 'components/field/EnumSelect';
import IntegerInput from 'components/field/IntegerInput';
import StringInput from 'components/field/StringInput';
import FormItem from 'components/form/FormItem';
import * as F from 'components/form/FormLayout.styled';
import Typography from 'components/global/Typography';

import useAddressData from 'hooks/useAddressData';

import DriverForm from 'pages/OTOFlexMain/Drivers/DriverForm';

import { AUTOCOMPLETE, ENUMS } from 'services/consts';

/**
 * @author @omerrkosar
 */

export const AreaDetails = ({ mode }) => {
	const viewMode = mode === 'view';

	const formMethods = useFormContext();
	const { countries, cities, fetchCitiesByCountry } = useAddressData();
	const driverAutocompleteColumns = useMemo(() => [{ accessor: 'name', width: 200 }], []);

	const {
		setValue,
		watch,
		formState: { isLoading: isFormLoading, isSubmitting }
	} = formMethods;

	const isLoading = isFormLoading || isSubmitting;

	const defaultColSpan = {
		md: 6
	};

	const countryName = watch('countryName');
	const cityName = watch('cityName');

	return (
		<>
			<FormItem colSpan={defaultColSpan} label="areaName" isLoading={isLoading} required>
				<StringInput name="areaName" disabled={viewMode} />
			</FormItem>
			<FormItem colSpan={defaultColSpan} label="country" isLoading={isLoading}>
				<EnumSelect
					labelPrefix="country."
					name="countryName"
					value={countryName ?? null}
					disabled={viewMode}
					data={countries}
					renderSelectionValue={(value) => (
						<F.FlagOptionContainer className="selected">
							<span className={`oto-country-flag flag-${value.split('.')[1]?.toLowerCase()}`} />
							<Typography>{value ?? null}</Typography>
						</F.FlagOptionContainer>
					)}
					renderOption={(value) => (
						<F.FlagOptionContainer>
							<span className={`oto-country-flag flag-${value?.toLowerCase()}`} />
							<Typography>{`country.${value?.toUpperCase()}`}</Typography>
						</F.FlagOptionContainer>
					)}
					setValue={(value) => {
						fetchCitiesByCountry(value);

						if (value !== countryName) {
							setValue(`cityName`, null);
						}

						setValue(`countryName`, value ?? null, {
							shouldValidate: true,
							shouldDirty: true
						});
					}}
				/>
			</FormItem>
			<FormItem colSpan={defaultColSpan} label="city" isLoading={isLoading}>
				<EnumSelect
					name="cityName"
					value={cityName ?? null}
					setValue={(value) => {
						setValue(`cityName`, value ?? null, {
							shouldValidate: true,
							shouldDirty: true
						});
					}}
					disabled={!countryName || viewMode}
					data={cities}
				/>
			</FormItem>

			<FormItem colSpan={defaultColSpan} label="capacity" isLoading={isLoading} required>
				<IntegerInput name="capacity" unit="pcs" disabled={viewMode} />
			</FormItem>
			<FormItem colSpan={defaultColSpan} label="priority" isLoading={isLoading} required>
				<EnumSelect
					valueRemovable={false}
					placeholder="selectOne"
					disabled={viewMode}
					searchable={false}
					name="priority"
					enumName={ENUMS.AREA_PRIORITY}
					value={watch('priority')}
					setValue={(value) => {
						setValue('priority', value ?? null, { shouldValidate: true, shouldDirty: true });
					}}
				/>
			</FormItem>

			<FormItem label="description" isLoading={isLoading}>
				<StringInput name="description" disabled={viewMode} />
			</FormItem>

			<FormItem label="otoFlexDrivers" isLoading={isLoading} roleName="OTOFlexModule">
				<AutoComplete
					name="drivers"
					disabled={viewMode}
					autoCompleteName={AUTOCOMPLETE.DRIVER_USERS}
					value={watch('drivers')}
					setValue={(value) => {
						setValue('drivers', value?.map((item) => item.ID) ?? null, {
							shouldValidate: true,
							shouldDirty: true
						});
					}}
					firstItem={{
						action: () =>
							NiceModal.show(DriverForm, {
								setDriver: (value) => {
									setValue(`drivers`, [...(watch('drivers') ?? []), value], {
										shouldValidate: true,
										shouldDirty: true
									});
								}
							}),
						label: <Typography color="var(--blue)">addANewDriverUser</Typography>
					}}
					columns={driverAutocompleteColumns}
					selectionLabelAccessor={['name']}
					multipleSelection
				/>
			</FormItem>
		</>
	);
};
